import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function xPlus(k) {
  if (k === 0) return "x";
  return k > 0 ? `x+${k}` : `x-${Math.abs(k)}`;
}

function x2PlusCx(c) {
  if (c === 1) return "x^2+x";
  if (c === -1) return "x^2-x";
  return c > 0 ? `x^2+${c}x` : `x^2${c}x`;
}

function generateMay2025OriginalTask() {
  return {
    type: "MaturaRationalExpressionClosedTask",
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 8,
    },
    data: {
      a: 2,
      c: 1,
      numExpanded: "x^2+x",
      denExpanded: "x^2+4x+4",
      linearA: "x+2",
      linearC: "x+1",
      exprLatex: "\\frac{x^2+x}{x^2+4x+4}\\cdot\\frac{x+2}{x}",
      options: [
        { label: "A", latex: "\\frac{x+2}{4x+4}", isCorrect: false },
        { label: "B", latex: "\\frac{x+1}{4x+5}", isCorrect: false },
        { label: "C", latex: "\\frac{x+1}{x+2}", isCorrect: true },
        { label: "D", latex: "\\frac{2x}{x+2}", isCorrect: false },
      ],
      correctIndex: 2,
      maxPoints: 1,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return generateMay2025OriginalTask();
  }

  const a = pick([2, 3, 4, 5, 6, 7]);
  // Po skróceniu ma pozostać rzeczywisty iloraz dwóch różnych czynników.
  // Dla c === a otrzymalibyśmy (x+a)/(x+a)=1, co zaciera sens zadania.
  const c = pick(
    [-7, -6, -5, -4, -3, -2, -1, 1, 2, 3, 4, 5, 6, 7].filter(
      (candidate) => candidate !== a
    )
  );

  const numExpanded = x2PlusCx(c);
  const denExpanded = `x^2+${2 * a}x+${a * a}`;
  const linearA = xPlus(a);
  const linearC = xPlus(c);

  const exprLatex = `\\frac{${numExpanded}}{${denExpanded}}\\cdot\\frac{${linearA}}{x}`;
  const correctLatex = `\\frac{${linearC}}{${linearA}}`;

  // Typowe błędy uczniowskie:
  // 1) Błędna zamiana trójmianu na wyrażenie liniowe.
  const wrong1 = `\\frac{${linearA}}{${2 * a}x+${a * a}}`;
  // 2) Prawidłowy licznik po skracaniu, ale błędny mianownik liniowy (+1).
  const wrong2 = `\\frac{${linearC}}{${2 * a}x+${a * a + 1}}`;
  // 3) Błędne skracanie zostawiające ax w liczniku.
  const wrong3 = `\\frac{${a}x}{${linearA}}`;

  const optionsRaw = [
    { latex: correctLatex, isCorrect: true },
    { latex: wrong1, isCorrect: false },
    { latex: wrong2, isCorrect: false },
    { latex: wrong3, isCorrect: false },
  ];

  const options = shuffle(optionsRaw).map((o, idx) => ({
    label: LETTERS[idx],
    latex: o.latex,
    isCorrect: o.isCorrect,
  }));

  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaRationalExpressionClosedTask",
    data: {
      a,
      c,
      numExpanded,
      denExpanded,
      linearA,
      linearC,
      exprLatex,
      options,
      correctIndex,
      maxPoints: 1,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - zamknięte.</strong>
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Dla każdej liczby rzeczywistej <InlineMath math={"x"} /> różnej od{" "}
        <InlineMath math={`(-${data.a})`} /> oraz różnej od{" "}
        <InlineMath math={"0"} /> wartość wyrażenia
      </p>
      <BlockMath math={data.exprLatex} />
      <p>jest równa wartości wyrażenia:</p>

      <div style={{ marginTop: 8 }}>
        {data.options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\;${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = data.options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  const letterOptions = data.options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];
  return <InlineMath math={`${correct.label}.\\;${correct.latex}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }
  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${userAnswer.label}.\\;${userAnswer.latex}`} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const { a, numExpanded, denExpanded, linearA, linearC, options, correctIndex } =
    data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1.</strong> Rozkładamy licznik i mianownik:
      </div>
      <BlockMath math={`${numExpanded}=x(${linearC})`} />
      <BlockMath math={`${denExpanded}=(${linearA})^2`} />

      <div>
        <strong>Krok 2.</strong> Podstawiamy i skracamy wspólne czynniki{" "}
        <InlineMath math={"x"} /> oraz <InlineMath math={`(${linearA})`} />:
      </div>
      <p>Możemy to zrobić, ponieważ w dziedzinie zadania <InlineMath math={`x\\ne 0,\\quad x\\ne -${a}`} />.</p>
      <BlockMath
        math={`\\begin{aligned}&\\frac{${numExpanded}}{${denExpanded}}\\cdot\\frac{${linearA}}{x}\\\\
        &=\\frac{x(${linearC})}{(${linearA})^2}\\cdot\\frac{${linearA}}{x}\\\\
        &=\\frac{${linearC}}{${linearA}}\\end{aligned}`}
      />

      <div>
        Poprawna odpowiedź:{" "}
        <InlineMath math={`${options[correctIndex].label}.\\;${options[correctIndex].latex}`} />
      </div>
    </div>
  );
}
